import { BowelMovement, MedicationScheduleEntry, MyMedication } from '../types';
import { DiseaseStateReportData } from './diseaseStateReportService';
import { HealthReportData } from './healthReportService';
import jsPDF from 'jspdf';
// @ts-ignore
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
// @ts-ignore
import html2pdf from 'html2pdf.js';

// Funkcja do zamiany polskich znaków na ASCII (dla starych eksportów tabelarycznych)
const replacePolishChars = (text: string): string => {
  return text
    .replace(/ą/g, 'a').replace(/ć/g, 'c').replace(/ę/g, 'e')
    .replace(/ł/g, 'l').replace(/ń/g, 'n').replace(/ó/g, 'o')
    .replace(/ś/g, 's').replace(/ź/g, 'z').replace(/ż/g, 'z')
    .replace(/Ą/g, 'A').replace(/Ć/g, 'C').replace(/Ę/g, 'E')
    .replace(/Ł/g, 'L').replace(/Ń/g, 'N').replace(/Ó/g, 'O')
    .replace(/Ś/g, 'S').replace(/Ź/g, 'Z').replace(/Ż/g, 'Z');
};

export const exportService = {
  /** Eksport elementu HTML do PDF (polskie znaki, wykresy – karty nie rozcinane) */
  exportReportElementToPDF: async (element: HTMLElement, filename: string = 'raport'): Promise<void> => {
    try {
      const fullFilename = `${filename}_${new Date().toISOString().split('T')[0]}.pdf`;
      // html2pdf.js ma pagebreak w API; typy npm mogą być niekompletne
      const opts = {
        margin: 12,
        filename: fullFilename,
        image: { type: 'png' as const, quality: 1 },
        html2canvas: {
          scale: 1.5,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          ignoreElements: (el: Element) => (el as HTMLElement).classList?.contains('no-pdf-export') ?? false
        },
        jsPDF: { unit: 'mm' as const, format: 'a4' as const, orientation: 'portrait' as const },
        pagebreak: {
          mode: ['css', 'legacy'] as const,
          avoid: ['.report-card', '.medication-card', '.kpi-card', '.panel', '.card']
        }
      };
      await html2pdf()
        .set(opts)
        .from(element)
        .save();
    } catch (err) {
      console.error('Błąd eksportu raportu do PDF:', err);
      alert('Nie udało się pobrać raportu. Spróbuj ponownie lub odśwież stronę.');
    }
  },

  // Eksport do CSV
  exportToCSV: (data: BowelMovement[], filename: string = 'wyproznienia') => {
    const headers = [
      'Data',
      'Godzina',
      'Konsystencja',
      'Ilość',
      'Krew',
      'Ilość krwi',
      'Rodzaj krwi',
      'Śluz',
      'Ilość śluzu',
      'Ból',
      'Gazy',
      'Intensywność gazów',
      'Pilność',
      'Inne objawy'
    ];

    const rows = data.map(entry => [
      entry.date,
      entry.time,
      entry.consistency,
      entry.amount,
      entry.hasBlood ? 'TAK' : 'NIE',
      entry.bloodAmount || '',
      entry.bloodType || '',
      entry.hasMucus ? 'TAK' : 'NIE',
      entry.mucusAmount || '',
      entry.pain,
      entry.hasGas ? 'TAK' : 'NIE',
      entry.gasIntensity || '',
      entry.urgency,
      entry.otherSymptoms || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  // Eksport do Excel
  exportToExcel: (data: BowelMovement[], filename: string = 'wyproznienia') => {
    const worksheetData = [
      [
        'Data',
        'Godzina',
        'Konsystencja',
        'Ilość',
        'Krew',
        'Ilość krwi',
        'Rodzaj krwi',
        'Śluz',
        'Ilość śluzu',
        'Ból',
        'Gazy',
        'Intensywność gazów',
        'Pilność',
        'Inne objawy'
      ],
      ...data.map(entry => [
        entry.date,
        entry.time,
        entry.consistency,
        entry.amount,
        entry.hasBlood ? 'TAK' : 'NIE',
        entry.bloodAmount || '',
        entry.bloodType || '',
        entry.hasMucus ? 'TAK' : 'NIE',
        entry.mucusAmount || '',
        entry.pain,
        entry.hasGas ? 'TAK' : 'NIE',
        entry.gasIntensity || '',
        entry.urgency,
        entry.otherSymptoms || ''
      ])
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Wypróżnienia');

    // Ustaw szerokość kolumn
    worksheet['!cols'] = [
      { wch: 12 }, // Data
      { wch: 8 },  // Godzina
      { wch: 12 }, // Konsystencja
      { wch: 8 },  // Ilość
      { wch: 6 },  // Krew
      { wch: 10 }, // Ilość krwi
      { wch: 12 }, // Rodzaj krwi
      { wch: 6 },  // Śluz
      { wch: 10 }, // Ilość śluzu
      { wch: 6 },  // Ból
      { wch: 6 },  // Gazy
      { wch: 15 }, // Intensywność gazów
      { wch: 8 },  // Pilność
      { wch: 20 }  // Inne objawy
    ];

    XLSX.writeFile(workbook, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
  },

  // Eksport do PDF
  exportToPDF: (data: BowelMovement[], filename: string = 'wyproznienia') => {
    const doc = new jsPDF('l', 'mm', 'a4'); // landscape orientation
    
    // Tytuł
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Raport wyproznien', 14, 15);
    
    // Data wygenerowania
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const generatedDate = new Date().toLocaleString('pl-PL');
    doc.text(`Wygenerowano: ${generatedDate}`, 14, 22);
    doc.text(`Liczba wpisow: ${data.length}`, 14, 27);

    // Przygotowanie danych do tabeli - zamień polskie znaki
    const tableData = data.map(entry => [
      entry.date,
      entry.time,
      replacePolishChars(entry.consistency),
      replacePolishChars(entry.amount),
      entry.hasBlood ? 'TAK' : 'NIE',
      entry.bloodAmount ? `${entry.bloodAmount}/5` : '',
      entry.bloodType ? replacePolishChars(entry.bloodType) : '',
      entry.hasMucus ? 'TAK' : 'NIE',
      entry.mucusAmount ? `${entry.mucusAmount}/5` : '',
      `${entry.pain}/5`,
      entry.hasGas ? 'TAK' : 'NIE',
      entry.gasIntensity ? `${entry.gasIntensity}/5` : '',
      `${entry.urgency}/5`,
      replacePolishChars(entry.otherSymptoms || '')
    ]);

    autoTable(doc, {
      head: [[
        'Data',
        'Godzina',
        'Konsystencja',
        'Ilosc',
        'Krew',
        'Ilosc krwi',
        'Rodzaj krwi',
        'Sluz',
        'Ilosc sluzu',
        'Bol',
        'Gazy',
        'Intensywnosc gazow',
        'Pilnosc',
        'Inne objawy'
      ]],
      body: tableData,
      startY: 32,
      styles: { 
        fontSize: 7,
        font: 'helvetica',
        fontStyle: 'normal',
        cellPadding: 2,
        overflow: 'linebreak',
        cellWidth: 'auto'
      },
      headStyles: { 
        fillColor: [76, 175, 80], 
        textColor: 255,
        font: 'helvetica',
        fontStyle: 'bold',
        fontSize: 7
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { top: 32, right: 14, bottom: 14, left: 14 },
      columnStyles: {
        0: { cellWidth: 20 }, // Data
        1: { cellWidth: 15 }, // Godzina
        2: { cellWidth: 20 }, // Konsystencja
        3: { cellWidth: 15 }, // Ilość
        4: { cellWidth: 12 }, // Krew
        5: { cellWidth: 15 }, // Ilość krwi
        6: { cellWidth: 18 }, // Rodzaj krwi
        7: { cellWidth: 12 }, // Śluz
        8: { cellWidth: 15 }, // Ilość śluzu
        9: { cellWidth: 12 }, // Ból
        10: { cellWidth: 12 }, // Gazy
        11: { cellWidth: 18 }, // Intensywność gazów
        12: { cellWidth: 12 }, // Pilność
        13: { cellWidth: 30 }  // Inne objawy
      }
    });

    doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
  },

  // Eksport statystyk i wykresów do PDF
  exportStatsToPDF: (data: BowelMovement[], filename: string = 'statystyki') => {
    const doc = new jsPDF('p', 'mm', 'a4'); // portrait orientation
    
    // Tytuł
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Statystyki i Wykresy', 14, 20);
    
    // Data wygenerowania
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const generatedDate = new Date().toLocaleString('pl-PL');
    doc.text(`Wygenerowano: ${generatedDate}`, 14, 27);
    doc.text(`Liczba wpisow: ${data.length}`, 14, 32);

    let startY = 40;

    // Statystyki ogólne
    const avgPain = data.length > 0 
      ? (data.reduce((sum, e) => sum + e.pain, 0) / data.length).toFixed(1)
      : '0';
    const avgUrgency = data.length > 0
      ? (data.reduce((sum, e) => sum + e.urgency, 0) / data.length).toFixed(1)
      : '0';
    const withBlood = data.filter(e => e.hasBlood).length;
    const withMucus = data.filter(e => e.hasMucus).length;
    const withGas = data.filter(e => e.hasGas).length;
    const maxPain = data.length > 0 ? Math.max(...data.map(e => e.pain)) : 0;
    const minPain = data.length > 0 ? Math.min(...data.map(e => e.pain)) : 0;
    const maxUrgency = data.length > 0 ? Math.max(...data.map(e => e.urgency)) : 0;
    const minUrgency = data.length > 0 ? Math.min(...data.map(e => e.urgency)) : 0;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Statystyki ogolne', 14, startY);
    startY += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Sredni bol: ${avgPain}/5 (min: ${minPain}, max: ${maxPain})`, 20, startY);
    startY += 7;
    doc.text(`Srednia pilnosc: ${avgUrgency}/5 (min: ${minUrgency}, max: ${maxUrgency})`, 20, startY);
    startY += 7;
    doc.text(`Z krwia: ${withBlood} (${data.length > 0 ? ((withBlood / data.length) * 100).toFixed(1) : 0}%)`, 20, startY);
    startY += 7;
    doc.text(`Ze sluzem: ${withMucus} (${data.length > 0 ? ((withMucus / data.length) * 100).toFixed(1) : 0}%)`, 20, startY);
    startY += 7;
    doc.text(`Z gazami: ${withGas} (${data.length > 0 ? ((withGas / data.length) * 100).toFixed(1) : 0}%)`, 20, startY);
    startY += 15;

    // Funkcja pomocnicza do dodawania tabeli
    const addTable = (head: string[][], body: string[][], title: string, color: [number, number, number]) => {
      if (startY > 250) {
        doc.addPage();
        startY = 20;
      }
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(title, 14, startY);
      startY += 10;

      autoTable(doc, {
        head: head,
        body: body,
        startY: startY,
        styles: { 
          fontSize: 9,
          font: 'helvetica',
          fontStyle: 'normal',
          cellPadding: 2
        },
        headStyles: { 
          fillColor: color, 
          textColor: 255,
          font: 'helvetica',
          fontStyle: 'bold',
          fontSize: 9
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: startY, right: 14, bottom: 14, left: 14 },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 40 },
          2: { cellWidth: 40 }
        }
      });

      startY = (doc as any).lastAutoTable.finalY + 15;
    };

    // Wykres konsystencji
    const consistencyCounts = {
      'plynna': data.filter(e => e.consistency === 'płynna').length,
      'miekka': data.filter(e => e.consistency === 'miękka').length,
      'normalna': data.filter(e => e.consistency === 'normalna').length,
      'twarda': data.filter(e => e.consistency === 'twarda').length,
    };

    const consistencyData = Object.entries(consistencyCounts).map(([key, value]) => [
      replacePolishChars(key.charAt(0).toUpperCase() + key.slice(1)),
      String(value),
      data.length > 0 ? `${((value / data.length) * 100).toFixed(1)}%` : '0%'
    ]);

    addTable([['Konsystencja', 'Liczba', 'Procent']], consistencyData, 'Konsystencja', [76, 175, 80]);

    // Rozkład ilości
    const amountCounts = {
      'mala': data.filter(e => e.amount === 'mała').length,
      'srednia': data.filter(e => e.amount === 'średnia').length,
      'duza': data.filter(e => e.amount === 'duża').length,
    };

    const amountData = Object.entries(amountCounts).map(([key, value]) => [
      replacePolishChars(key.charAt(0).toUpperCase() + key.slice(1)),
      String(value),
      data.length > 0 ? `${((value / data.length) * 100).toFixed(1)}%` : '0%'
    ]);

    addTable([['Ilosc', 'Liczba', 'Procent']], amountData, 'Ilosc', [255, 152, 0]);

    // Rozkład bólu (0-5)
    const painDistribution: { [key: number]: number } = {};
    for (let i = 0; i <= 5; i++) {
      painDistribution[i] = data.filter(e => e.pain === i).length;
    }

    const painData = Object.entries(painDistribution)
      .filter(([_, count]) => count > 0)
      .map(([level, count]) => [
        String(level),
        String(count),
        data.length > 0 ? `${((Number(count) / data.length) * 100).toFixed(1)}%` : '0%'
      ]);

    if (painData.length > 0) {
      addTable([['Poziom bolu', 'Liczba', 'Procent']], painData, 'Rozklad bolu (0-5)', [244, 67, 54]);
    }

    // Rozkład pilności (0-5)
    const urgencyDistribution: { [key: number]: number } = {};
    for (let i = 0; i <= 5; i++) {
      urgencyDistribution[i] = data.filter(e => e.urgency === i).length;
    }

    const urgencyData = Object.entries(urgencyDistribution).map(([level, count]) => [
      String(level),
      String(count),
      data.length > 0 ? `${((Number(count) / data.length) * 100).toFixed(1)}%` : '0%'
    ]);

    addTable([['Poziom pilnosci', 'Liczba', 'Procent']], urgencyData, 'Rozklad pilnosci (0-5)', [156, 39, 176]);

    // Statystyki krwi
    if (withBlood > 0) {
      const bloodAmountDistribution: { [key: number]: number } = {};
      for (let i = 0; i <= 5; i++) {
        bloodAmountDistribution[i] = data.filter(e => e.hasBlood && e.bloodAmount === i).length;
      }

      const bloodAmountData = Object.entries(bloodAmountDistribution)
        .filter(([_, count]) => count > 0)
        .map(([level, count]) => [
          String(level),
          String(count),
          withBlood > 0 ? `${((Number(count) / withBlood) * 100).toFixed(1)}%` : '0%'
        ]);

      if (bloodAmountData.length > 0) {
        addTable([['Ilosc krwi (0-5)', 'Liczba', 'Procent']], bloodAmountData, 'Statystyki krwi - Ilosc', [244, 67, 54]);

        const bloodTypeCounts: { [key: string]: number } = {
          'jasna': data.filter(e => e.hasBlood && e.bloodType === 'jasna').length,
          'zywa': data.filter(e => e.hasBlood && e.bloodType === 'żywa').length,
          'ciemna': data.filter(e => e.hasBlood && e.bloodType === 'ciemna').length,
          'wymieszana': data.filter(e => e.hasBlood && e.bloodType === 'wymieszana').length,
        };

        const bloodTypeData = Object.entries(bloodTypeCounts)
          .filter(([_, count]) => count > 0)
          .map(([type, count]) => [
            replacePolishChars(type.charAt(0).toUpperCase() + type.slice(1)),
            String(count),
            withBlood > 0 ? `${((Number(count) / withBlood) * 100).toFixed(1)}%` : '0%'
          ]);

        if (bloodTypeData.length > 0) {
          addTable([['Rodzaj krwi', 'Liczba', 'Procent']], bloodTypeData, 'Statystyki krwi - Rodzaj', [244, 67, 54]);
        }
      }
    }

    // Statystyki śluzu
    if (withMucus > 0) {
      const mucusAmountDistribution: { [key: number]: number } = {};
      for (let i = 0; i <= 5; i++) {
        mucusAmountDistribution[i] = data.filter(e => e.hasMucus && e.mucusAmount === i).length;
      }

      const mucusData = Object.entries(mucusAmountDistribution)
        .filter(([_, count]) => count > 0)
        .map(([level, count]) => [
          String(level),
          String(count),
          withMucus > 0 ? `${((Number(count) / withMucus) * 100).toFixed(1)}%` : '0%'
        ]);

      if (mucusData.length > 0) {
        addTable([['Ilosc sluzu (0-5)', 'Liczba', 'Procent']], mucusData, 'Statystyki sluzu', [33, 150, 243]);
      }
    }

    // Statystyki gazów
    if (withGas > 0) {
      const gasIntensityDistribution: { [key: number]: number } = {};
      for (let i = 0; i <= 5; i++) {
        gasIntensityDistribution[i] = data.filter(e => e.hasGas && e.gasIntensity === i).length;
      }

      const gasData = Object.entries(gasIntensityDistribution)
        .filter(([_, count]) => count > 0)
        .map(([level, count]) => [
          String(level),
          String(count),
          withGas > 0 ? `${((Number(count) / withGas) * 100).toFixed(1)}%` : '0%'
        ]);

      if (gasData.length > 0) {
        addTable([['Intensywnosc gazow (0-5)', 'Liczba', 'Procent']], gasData, 'Statystyki gazow', [255, 193, 7]);
      }
    }

    // Wykres ostatnich 7 dni
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - i));
      return date.toISOString().split('T')[0];
    });

    const dailyCounts = last7Days.map(date => {
      return data.filter(e => e.date === date).length;
    });

    if (startY > 250) {
      doc.addPage();
      startY = 20;
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Liczba wyproznien w ostatnich 7 dniach', 14, startY);
    startY += 10;

    const dailyData = last7Days.map((date, index) => {
      const dayName = new Date(date).toLocaleDateString('pl-PL', { weekday: 'short' });
      const dayDate = new Date(date).toLocaleDateString('pl-PL', { day: 'numeric', month: 'numeric' });
      return [
        `${replacePolishChars(dayName)} ${dayDate}`,
        String(dailyCounts[index])
      ];
    });

    autoTable(doc, {
      head: [['Data', 'Liczba']],
      body: dailyData,
      startY: startY,
      styles: { 
        fontSize: 10,
        font: 'helvetica',
        fontStyle: 'normal',
        cellPadding: 3
      },
      headStyles: { 
        fillColor: [33, 150, 243], 
        textColor: 255,
        font: 'helvetica',
        fontStyle: 'bold',
        fontSize: 10
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { top: startY, right: 14, bottom: 14, left: 14 },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 40 }
      }
    });

    doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
  },

  // Eksport przyjętych leków do PDF dla danego miesiąca
  exportMedicationsToPDF: (
    entries: MedicationScheduleEntry[], 
    myMedications: MyMedication[],
    monthName: string,
    year: number,
    filename: string = 'raport_lekow'
  ) => {
    // Filtruj tylko przyjęte leki
    const takenEntries = entries.filter(e => e.taken);
    
    if (takenEntries.length === 0) {
      alert('Brak przyjętych leków w tym miesiącu.');
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4'); // landscape orientation
    
    // Tytuł
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`Raport przyjetych lekow - ${replacePolishChars(monthName)} ${year}`, 14, 15);
    
    // Data wygenerowania
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const generatedDate = new Date().toLocaleString('pl-PL');
    doc.text(`Wygenerowano: ${generatedDate}`, 14, 22);
    doc.text(`Liczba przyjetych lekow: ${takenEntries.length}`, 14, 27);

    // Przygotowanie danych do tabeli
    const tableData = takenEntries
      .sort((a, b) => {
        // Sortuj po dacie, potem po godzinie
        if (a.date !== b.date) {
          return a.date.localeCompare(b.date);
        }
        return a.time.localeCompare(b.time);
      })
      .map(entry => {
        const medication = myMedications.find(m => m.id === entry.myMedicationId);
        const medicationName = medication?.name || 'Nieznany lek';
        const takenAt = entry.takenAt 
          ? new Date(entry.takenAt).toLocaleString('pl-PL', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })
          : '-';
        
        return [
          entry.date,
          entry.time,
          replacePolishChars(medicationName),
          `${entry.dosage} szt`,
          takenAt,
          medication?.withMeal ? 'TAK' : 'NIE'
        ];
      });

    autoTable(doc, {
      head: [[
        'Data',
        'Godzina planowana',
        'Nazwa leku',
        'Dawka',
        'Data i godzina przyjecia',
        'W trakcie posilku'
      ]],
      body: tableData,
      startY: 32,
      styles: { 
        fontSize: 9,
        font: 'helvetica',
        fontStyle: 'normal',
        cellPadding: 3,
        overflow: 'linebreak',
        cellWidth: 'auto'
      },
      headStyles: { 
        fillColor: [33, 150, 243], 
        textColor: 255,
        font: 'helvetica',
        fontStyle: 'bold',
        fontSize: 9
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { top: 32, right: 14, bottom: 14, left: 14 },
      columnStyles: {
        0: { cellWidth: 25 }, // Data
        1: { cellWidth: 30 }, // Godzina planowana
        2: { cellWidth: 50 }, // Nazwa leku
        3: { cellWidth: 20 }, // Dawka
        4: { cellWidth: 40 }, // Data i godzina przyjęcia
        5: { cellWidth: 30 }  // W trakcie posiłku
      }
    });

    // Podsumowanie na końcu
    const totalByMedication = new Map<string, number>();
    takenEntries.forEach(entry => {
      const medication = myMedications.find(m => m.id === entry.myMedicationId);
      const medicationName = medication?.name || 'Nieznany lek';
      const current = totalByMedication.get(medicationName) || 0;
      totalByMedication.set(medicationName, current + entry.dosage);
    });

    const summaryData = Array.from(totalByMedication.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, total]) => [
        replacePolishChars(name),
        `${total} szt`
      ]);

    if (summaryData.length > 0) {
      const finalY = (doc as any).lastAutoTable.finalY || 100;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Podsumowanie przyjetych lekow:', 14, finalY + 15);
      
      autoTable(doc, {
        head: [['Nazwa leku', 'Laczna liczba przyjetych sztuk']],
        body: summaryData,
        startY: finalY + 22,
        styles: { 
          fontSize: 9,
          font: 'helvetica',
          fontStyle: 'normal',
          cellPadding: 3
        },
        headStyles: { 
          fillColor: [76, 175, 80], 
          textColor: 255,
          font: 'helvetica',
          fontStyle: 'bold',
          fontSize: 9
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: finalY + 22, right: 14, bottom: 14, left: 14 },
        columnStyles: {
          0: { cellWidth: 120 },
          1: { cellWidth: 60 }
        }
      });
    }

    doc.save(`${filename}_${monthName.toLowerCase()}_${year}_${new Date().toISOString().split('T')[0]}.pdf`);
  },

  // Eksport danych samopoczucia do PDF
  exportWellbeingToPDF: (
    entries: any[],
    type: 'sleep' | 'stress' | 'energy' | 'activity' | 'intestines' | 'stomach' | 'food' | 'all',
    filename: string = 'raport_samopoczucie',
    periodLabel: string = ''
  ) => {
    if (entries.length === 0) {
      alert(`Brak danych do wygenerowania raportu.`);
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4'); // landscape orientation
    
    // Tytuł
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    const titleMap: Record<string, string> = {
      sleep: 'Raport snu',
      stress: 'Raport stresu',
      energy: 'Raport energii',
      activity: 'Raport aktywności',
      intestines: 'Raport jelit',
      stomach: 'Raport żołądka',
      food: 'Raport jedzenia',
      all: 'Raport samopoczucia - wszystkie dane'
    };
    doc.text(replacePolishChars(titleMap[type]), 14, 15);
    
    // Data wygenerowania
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const generatedDate = new Date().toLocaleString('pl-PL');
    doc.text(`Wygenerowano: ${replacePolishChars(generatedDate)}`, 14, 22);
    doc.text(`Okres: ${replacePolishChars(periodLabel)}`, 14, 27);
    doc.text(replacePolishChars(`Liczba wpisów: ${entries.length}`), 14, 32);

    // Przygotowanie danych do tabeli
    let tableData: string[][];
    let headers: string[];

    if (type === 'sleep') {
      headers = [replacePolishChars('Data'), replacePolishChars('Godziny snu')];
      tableData = entries
        .sort((a, b) => b.date.localeCompare(a.date))
        .map(entry => [
          entry.date,
          (entry.sleepHours !== undefined && entry.sleepHours > 0) ? `${entry.sleepHours}h` : '-'
        ]);
    } else if (type === 'stress') {
      headers = [replacePolishChars('Data'), replacePolishChars('Poziom stresu'), replacePolishChars('Powód stresu')];
      tableData = entries
        .sort((a, b) => b.date.localeCompare(a.date))
        .map(entry => [
          entry.date,
          (entry.stressLevel !== undefined && entry.stressLevel > 0) ? `${entry.stressLevel}/5` : '-',
          replacePolishChars(entry.stressReason || '-')
        ]);
    } else if (type === 'energy') {
      headers = [replacePolishChars('Data'), replacePolishChars('Poziom energii')];
      tableData = entries
        .sort((a, b) => b.date.localeCompare(a.date))
        .map(entry => {
          const level = entry.energyLevel || 0;
          if (level === 0) return [entry.date, '-'];
          const levelLabel = ['Zmęczony', 'Niski', 'Średni', 'Dobry', 'Wysoki', 'Super'][level] || 'Nie ustawiono';
          return [entry.date, `${level}/5 (${replacePolishChars(levelLabel)})`];
        });
    } else if (type === 'activity') {
      headers = [replacePolishChars('Data'), replacePolishChars('Poziom aktywności')];
      tableData = entries
        .sort((a, b) => b.date.localeCompare(a.date))
        .map(entry => [
          entry.date,
          (entry.activityLevel !== undefined && entry.activityLevel > 0) ? `${entry.activityLevel}/5` : '-'
        ]);
    } else if (type === 'stomach') {
      headers = [replacePolishChars('Data'), replacePolishChars('Zgaga'), replacePolishChars('Nudności'), replacePolishChars('Wymioty'), replacePolishChars('Ból żołądka')];
      tableData = entries
        .sort((a, b) => b.date.localeCompare(a.date))
        .map(entry => {
          const h = entry.heartburnLevel ?? (entry.heartburn ? 1 : 0);
          const n = entry.nauseaLevel ?? (entry.nausea ? 1 : 0);
          const v = entry.vomitingLevel ?? (entry.vomiting ? 1 : 0);
          const p = entry.stomachPainLevel ?? (entry.stomachPainBool ? 1 : 0);
          return [
            entry.date,
            h > 0 ? `${h}/5` : '-',
            n > 0 ? `${n}/5` : '-',
            v > 0 ? `${v}/5` : '-',
            p > 0 ? `${p}/5` : '-'
          ];
        });
    } else if (type === 'food') {
      headers = [replacePolishChars('Data'), replacePolishChars('Zmiana'), replacePolishChars('Ciężkość'), replacePolishChars('Alkohol')];
      tableData = entries
        .sort((a, b) => b.date.localeCompare(a.date))
        .map(entry => [
          entry.date,
          (entry.foodChange ?? 0) > 0 ? `${entry.foodChange}/5` : '-',
          (entry.foodHeaviness ?? 0) > 0 ? `${entry.foodHeaviness}/5` : '-',
          (entry.alcoholLevel ?? 0) > 0 ? `${entry.alcoholLevel}/5` : '-'
        ]);
    } else if (type === 'all') {
      headers = ['Data', 'Sen', 'Stres', 'Energia', replacePolishChars('Aktywność'), replacePolishChars('Przelewanie'), replacePolishChars('Wzdęcie'), replacePolishChars('Ból jelit'), 'Gazy', replacePolishChars('Żołądek'), 'Jedzenie', replacePolishChars('Powód stresu'), 'Notatka'];
      tableData = entries
        .sort((a, b) => b.date.localeCompare(a.date))
        .map(entry => {
          const h = entry.heartburnLevel ?? (entry.heartburn ? 1 : 0);
          const n = entry.nauseaLevel ?? (entry.nausea ? 1 : 0);
          const v = entry.vomitingLevel ?? (entry.vomiting ? 1 : 0);
          const p = entry.stomachPainLevel ?? (entry.stomachPainBool ? 1 : 0);
          const stomachParts = [];
          if (h > 0) stomachParts.push(`Zg:${h}`);
          if (n > 0) stomachParts.push(`Nud:${n}`);
          if (v > 0) stomachParts.push(`Wym:${v}`);
          if (p > 0) stomachParts.push(`Ból:${p}`);
          const stomachStr = stomachParts.length > 0 ? stomachParts.join(', ') : '-';
          const foodStr = [entry.foodChange, entry.foodHeaviness, entry.alcoholLevel].some(x => (x ?? 0) > 0)
            ? `${entry.foodChange ?? 0}/${entry.foodHeaviness ?? 0}/${entry.alcoholLevel ?? 0}` : '-';
          const customNote = entry.notes || entry.customNote || '';
          const noteStr = customNote ? (customNote.length > 25 ? customNote.substring(0, 25) + '...' : customNote) : '-';
          return [
            entry.date,
            (entry.sleepHours !== undefined && entry.sleepHours > 0) ? `${entry.sleepHours}h` : '-',
            (entry.stressLevel !== undefined && entry.stressLevel > 0) ? `${entry.stressLevel}/5` : '-',
            (entry.energyLevel !== undefined && entry.energyLevel > 0) ? `${entry.energyLevel}/5` : '-',
            (entry.activityLevel !== undefined && entry.activityLevel > 0) ? `${entry.activityLevel}/5` : '-',
            (entry.gurgling !== undefined && entry.gurgling > 0) ? `${entry.gurgling}/5` : '-',
            (entry.bloating !== undefined && entry.bloating > 0) ? `${entry.bloating}/5` : '-',
            (entry.stomachPain !== undefined && entry.stomachPain > 0) ? `${entry.stomachPain}/5` : '-',
            (entry.gasLevel !== undefined && entry.gasLevel > 0) ? `${entry.gasLevel}/5` : '-',
            replacePolishChars(stomachStr),
            replacePolishChars(foodStr),
            replacePolishChars(entry.stressReason || '-'),
            replacePolishChars(noteStr)
          ];
        });
    } else { // intestines
      headers = [replacePolishChars('Data'), replacePolishChars('Przelewanie'), replacePolishChars('Wzdęcie'), replacePolishChars('Ból jelit'), 'Gazy'];
      tableData = entries
        .sort((a, b) => b.date.localeCompare(a.date))
        .map(entry => [
          entry.date,
          (entry.gurgling !== undefined && entry.gurgling > 0) ? `${entry.gurgling}/5` : '-',
          (entry.bloating !== undefined && entry.bloating > 0) ? `${entry.bloating}/5` : '-',
          (entry.stomachPain !== undefined && entry.stomachPain > 0) ? `${entry.stomachPain}/5` : '-',
          (entry.gasLevel !== undefined && entry.gasLevel > 0) ? `${entry.gasLevel}/5` : '-'
        ]);
    }

    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 40,
      styles: { 
        fontSize: 9,
        font: 'helvetica',
        fontStyle: 'normal',
        cellPadding: 3,
        overflow: 'linebreak',
        cellWidth: 'auto'
      },
      headStyles: { 
        fillColor: [107, 127, 255], 
        textColor: 255,
        font: 'helvetica',
        fontStyle: 'bold',
        fontSize: 9
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { top: 40, right: 14, bottom: 14, left: 14 },
      columnStyles: type === 'stress' 
        ? {
            0: { cellWidth: 40 }, // Data
            1: { cellWidth: 30 }, // Poziom stresu
            2: { cellWidth: 120 } // Powód stresu
          }
        : type === 'intestines'
        ? {
            0: { cellWidth: 35 }, // Data
            1: { cellWidth: 40 }, // Przelewanie
            2: { cellWidth: 40 }, // Wzdęcie
            3: { cellWidth: 40 }, // Ból jelit
            4: { cellWidth: 35 }  // Gazy
          }
        : type === 'stomach'
        ? {
            0: { cellWidth: 35 },
            1: { cellWidth: 40 },
            2: { cellWidth: 40 },
            3: { cellWidth: 40 },
            4: { cellWidth: 45 }
          }
        : type === 'all'
        ? {
            0: { cellWidth: 22 }, 1: { cellWidth: 18 }, 2: { cellWidth: 18 }, 3: { cellWidth: 18 }, 4: { cellWidth: 22 },
            5: { cellWidth: 22 }, 6: { cellWidth: 22 }, 7: { cellWidth: 22 }, 8: { cellWidth: 18 }, 9: { cellWidth: 35 },
            10: { cellWidth: 25 }, 11: { cellWidth: 35 }, 12: { cellWidth: 35 }
          }
        : {
            0: { cellWidth: 60 }, // Data
            1: { cellWidth: 130 } // Wartość
          }
    });

    // Statystyki
    const finalY = (doc as any).lastAutoTable.finalY || 100;
    let startY = finalY + 15;

    if (startY > 180) {
      doc.addPage();
      startY = 20;
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(replacePolishChars('Statystyki'), 14, startY);
    startY += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    if (type === 'sleep') {
      const sleepValues = entries.map(e => e.sleepHours || 0).filter(v => v > 0);
      if (sleepValues.length > 0) {
        const avgSleep = (sleepValues.reduce((a, b) => a + b, 0) / sleepValues.length).toFixed(1);
        const minSleep = Math.min(...sleepValues);
        const maxSleep = Math.max(...sleepValues);
        doc.text(replacePolishChars(`Średnia ilość snu: ${avgSleep}h`), 20, startY);
        startY += 7;
        doc.text(`Min: ${minSleep}h, Max: ${maxSleep}h`, 20, startY);
      }
    } else if (type === 'stress') {
      const stressValues = entries.map(e => e.stressLevel || 0);
      if (stressValues.length > 0) {
        const avgStress = (stressValues.reduce((a, b) => a + b, 0) / stressValues.length).toFixed(1);
        const minStress = Math.min(...stressValues);
        const maxStress = Math.max(...stressValues);
        doc.text(replacePolishChars(`Średni poziom stresu: ${avgStress}/5`), 20, startY);
        startY += 7;
        doc.text(`Min: ${minStress}/5, Max: ${maxStress}/5`, 20, startY);
        startY += 7;
        const withReason = entries.filter(e => e.stressReason).length;
        doc.text(replacePolishChars(`Wpisy z powodem stresu: ${withReason} (${((withReason / entries.length) * 100).toFixed(1)}%)`), 20, startY);
      }
    } else if (type === 'energy') {
      const energyValues = entries.map(e => e.energyLevel || 0);
      if (energyValues.length > 0) {
        const avgEnergy = (energyValues.reduce((a, b) => a + b, 0) / energyValues.length).toFixed(1);
        const minEnergy = Math.min(...energyValues);
        const maxEnergy = Math.max(...energyValues);
        doc.text(replacePolishChars(`Średni poziom energii: ${avgEnergy}/5`), 20, startY);
        startY += 7;
        doc.text(`Min: ${minEnergy}/5, Max: ${maxEnergy}/5`, 20, startY);
      }
    } else if (type === 'activity') {
      const activityValues = entries.map(e => e.activityLevel ?? 0).filter(v => v > 0);
      if (activityValues.length > 0) {
        const avg = (activityValues.reduce((a, b) => a + b, 0) / activityValues.length).toFixed(1);
        doc.text(replacePolishChars(`Średni poziom aktywności: ${avg}/5`), 20, startY);
        startY += 7;
        doc.text(`Min: ${Math.min(...activityValues)}/5, Max: ${Math.max(...activityValues)}/5`, 20, startY);
      }
    } else if (type === 'stomach') {
      const h = entries.map(e => e.heartburnLevel ?? (e.heartburn ? 1 : 0)).filter(x => x > 0);
      const n = entries.map(e => e.nauseaLevel ?? (e.nausea ? 1 : 0)).filter(x => x > 0);
      const vom = entries.map(e => e.vomitingLevel ?? (e.vomiting ? 1 : 0)).filter(x => x > 0);
      const p = entries.map(e => e.stomachPainLevel ?? (e.stomachPainBool ? 1 : 0)).filter(x => x > 0);
      if (h.length > 0) { doc.text(replacePolishChars(`Zgaga - Średnia: ${(h.reduce((a,b)=>a+b,0)/h.length).toFixed(1)}/5`), 20, startY); startY += 7; }
      if (n.length > 0) { doc.text(replacePolishChars(`Nudności - Średnia: ${(n.reduce((a,b)=>a+b,0)/n.length).toFixed(1)}/5`), 20, startY); startY += 7; }
      if (vom.length > 0) { doc.text(replacePolishChars(`Wymioty - Średnia: ${(vom.reduce((a,b)=>a+b,0)/vom.length).toFixed(1)}/5`), 20, startY); startY += 7; }
      if (p.length > 0) { doc.text(replacePolishChars(`Ból żołądka - Średnia: ${(p.reduce((a,b)=>a+b,0)/p.length).toFixed(1)}/5`), 20, startY); startY += 7; }
    } else if (type === 'food') {
      const fc = entries.map(e => e.foodChange ?? 0).filter(v => v > 0);
      const fh = entries.map(e => e.foodHeaviness ?? 0).filter(v => v > 0);
      const al = entries.map(e => e.alcoholLevel ?? 0).filter(v => v > 0);
      if (fc.length > 0) { doc.text(replacePolishChars(`Zmiana - Średnia: ${(fc.reduce((a,b)=>a+b,0)/fc.length).toFixed(1)}/5`), 20, startY); startY += 7; }
      if (fh.length > 0) { doc.text(replacePolishChars(`Ciężkość - Średnia: ${(fh.reduce((a,b)=>a+b,0)/fh.length).toFixed(1)}/5`), 20, startY); startY += 7; }
      if (al.length > 0) { doc.text(replacePolishChars(`Alkohol - Średnia: ${(al.reduce((a,b)=>a+b,0)/al.length).toFixed(1)}/5`), 20, startY); startY += 7; }
    } else if (type === 'all') {
      // Sen
      const sleepValues = entries.map(e => e.sleepHours || 0).filter(v => v > 0);
      if (sleepValues.length > 0) {
        const avgSleep = (sleepValues.reduce((a, b) => a + b, 0) / sleepValues.length).toFixed(1);
        doc.text(replacePolishChars(`Sen - Średnia: ${avgSleep}h`), 20, startY);
        startY += 7;
      }
      
      // Stres
      const stressValues = entries.map(e => e.stressLevel || 0);
      if (stressValues.length > 0) {
        const avgStress = (stressValues.reduce((a, b) => a + b, 0) / stressValues.length).toFixed(1);
        doc.text(replacePolishChars(`Stres - Średnia: ${avgStress}/5`), 20, startY);
        startY += 7;
      }
      
      // Energia
      const energyValues = entries.map(e => e.energyLevel || 0);
      if (energyValues.length > 0) {
        const avgEnergy = (energyValues.reduce((a, b) => a + b, 0) / energyValues.length).toFixed(1);
        doc.text(replacePolishChars(`Energia - Średnia: ${avgEnergy}/5`), 20, startY);
        startY += 7;
      }
      
      // Aktywność
      const activityValues = entries.map(e => e.activityLevel ?? 0).filter(v => v > 0);
      if (activityValues.length > 0) {
        doc.text(replacePolishChars(`Aktywność - Średnia: ${(activityValues.reduce((a,b)=>a+b,0)/activityValues.length).toFixed(1)}/5`), 20, startY);
        startY += 7;
      }
      
      // Jelita
      const gurglingValues = entries.map(e => e.gurgling !== undefined ? e.gurgling : 0).filter(v => v > 0);
      const bloatingValues = entries.map(e => e.bloating !== undefined ? e.bloating : 0).filter(v => v > 0);
      const painValues = entries.map(e => e.stomachPain !== undefined ? e.stomachPain : 0).filter(v => v > 0);
      
      const gasValuesAll = entries.map(e => e.gasLevel ?? 0).filter(v => v > 0);
      if (gurglingValues.length > 0 || bloatingValues.length > 0 || painValues.length > 0 || gasValuesAll.length > 0) {
        doc.text(replacePolishChars('Jelita:'), 20, startY);
        startY += 7;
        if (gurglingValues.length > 0) {
          const avgGurgling = (gurglingValues.reduce((a, b) => a + b, 0) / gurglingValues.length).toFixed(1);
          doc.text(replacePolishChars(`  Przelewanie - Średnia: ${avgGurgling}/5`), 25, startY);
          startY += 7;
        }
        if (bloatingValues.length > 0) {
          const avgBloating = (bloatingValues.reduce((a, b) => a + b, 0) / bloatingValues.length).toFixed(1);
          doc.text(replacePolishChars(`  Wzdęcie - Średnia: ${avgBloating}/5`), 25, startY);
          startY += 7;
        }
        if (painValues.length > 0) {
          const avgPain = (painValues.reduce((a, b) => a + b, 0) / painValues.length).toFixed(1);
          doc.text(replacePolishChars(`  Ból jelit - Średnia: ${avgPain}/5`), 25, startY);
          startY += 7;
        }
        if (gasValuesAll.length > 0) {
          doc.text(replacePolishChars(`  Gazy - Średnia: ${(gasValuesAll.reduce((a,b)=>a+b,0)/gasValuesAll.length).toFixed(1)}/5`), 25, startY);
          startY += 7;
        }
      }
      
      // Żołądek
      const stomachEntries = entries.filter(e => {
        const h = e.heartburnLevel ?? (e.heartburn ? 1 : 0);
        const n = e.nauseaLevel ?? (e.nausea ? 1 : 0);
        const v = e.vomitingLevel ?? (e.vomiting ? 1 : 0);
        const p = e.stomachPainLevel ?? (e.stomachPainBool ? 1 : 0);
        return h > 0 || n > 0 || v > 0 || p > 0 || e.otherStomachSymptoms;
      });
      if (stomachEntries.length > 0) {
        doc.text(replacePolishChars(`Żołądek - Wpisy z objawami: ${stomachEntries.length} (${((stomachEntries.length / entries.length) * 100).toFixed(1)}%)`), 20, startY);
        startY += 7;
      }
      
      // Jedzenie
      const foodEntries = entries.filter(e => (e.foodChange ?? 0) > 0 || (e.foodHeaviness ?? 0) > 0 || (e.alcoholLevel ?? 0) > 0);
      if (foodEntries.length > 0) {
        doc.text(replacePolishChars(`Jedzenie - Wpisy z danymi: ${foodEntries.length} (${((foodEntries.length / entries.length) * 100).toFixed(1)}%)`), 20, startY);
        startY += 7;
      }
    } else { // intestines
      const gurglingValues = entries.map(e => e.gurgling !== undefined ? e.gurgling : 0).filter(v => v > 0);
      const bloatingValues = entries.map(e => e.bloating !== undefined ? e.bloating : 0).filter(v => v > 0);
      const painValues = entries.map(e => e.stomachPain !== undefined ? e.stomachPain : 0).filter(v => v > 0);
      const gasValues = entries.map(e => e.gasLevel !== undefined ? e.gasLevel : 0).filter(v => v > 0);
      
      if (gurglingValues.length > 0) {
        const avgGurgling = (gurglingValues.reduce((a, b) => a + b, 0) / gurglingValues.length).toFixed(1);
        const minGurgling = Math.min(...gurglingValues);
        const maxGurgling = Math.max(...gurglingValues);
        doc.text(replacePolishChars(`Przelewanie - Średnia: ${avgGurgling}/5, Min: ${minGurgling}/5, Max: ${maxGurgling}/5`), 20, startY);
        startY += 7;
      }
      
      if (bloatingValues.length > 0) {
        const avgBloating = (bloatingValues.reduce((a, b) => a + b, 0) / bloatingValues.length).toFixed(1);
        const minBloating = Math.min(...bloatingValues);
        const maxBloating = Math.max(...bloatingValues);
        doc.text(replacePolishChars(`Wzdęcie - Średnia: ${avgBloating}/5, Min: ${minBloating}/5, Max: ${maxBloating}/5`), 20, startY);
        startY += 7;
      }
      
      if (painValues.length > 0) {
        const avgPain = (painValues.reduce((a, b) => a + b, 0) / painValues.length).toFixed(1);
        const minPain = Math.min(...painValues);
        const maxPain = Math.max(...painValues);
        doc.text(replacePolishChars(`Ból jelit - Średnia: ${avgPain}/5, Min: ${minPain}/5, Max: ${maxPain}/5`), 20, startY);
        startY += 7;
      }
      
      if (gasValues.length > 0) {
        const avgGas = (gasValues.reduce((a, b) => a + b, 0) / gasValues.length).toFixed(1);
        doc.text(replacePolishChars(`Gazy - Średnia: ${avgGas}/5, Min: ${Math.min(...gasValues)}/5, Max: ${Math.max(...gasValues)}/5`), 20, startY);
        startY += 7;
      }
    }

    doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
  },

  /** Eksport historii samopoczucia (wellbeing_entries) do PDF - pełna struktura formularza */
  exportWellbeingHistoryToPDF: (
    entries: Array<{ date: string } & Record<string, unknown>>,
    filename: string = 'raport_samopoczucie',
    periodLabel: string = ''
  ): void => {
    if (entries.length === 0) {
      return;
    }
    const doc = new jsPDF('l', 'mm', 'a4');
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(replacePolishChars('Raport samopoczucia'), 14, 15);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const generatedDate = new Date().toLocaleString('pl-PL');
    doc.text(`Wygenerowano: ${replacePolishChars(generatedDate)}`, 14, 22);
    doc.text(`Okres: ${replacePolishChars(periodLabel)}`, 14, 27);
    doc.text(replacePolishChars(`Liczba wpisow: ${entries.length}`), 14, 32);

    const headers = [
      replacePolishChars('Data'),
      replacePolishChars('Nastrój'),
      replacePolishChars('Stres'),
      replacePolishChars('Powód stresu'),
      replacePolishChars('Energia'),
      replacePolishChars('Sen(h)'),
      replacePolishChars('Jakość snu'),
      replacePolishChars('Ból brzucha'),
      replacePolishChars('Wzdęcia'),
      replacePolishChars('Przelewanie'),
      replacePolishChars('Zgaga'),
      replacePolishChars('Nudności'),
      replacePolishChars('Wymioty'),
      replacePolishChars('Brak apetytu'),
      replacePolishChars('Pełność'),
      replacePolishChars('Rumień guz.'),
      replacePolishChars('Piodermia'),
      replacePolishChars('Skóra inne'),
      replacePolishChars('Oczy'),
      replacePolishChars('Afty'),
      replacePolishChars('Zmiana jedz.'),
      replacePolishChars('Ciężkość'),
      replacePolishChars('Alkohol'),
      replacePolishChars('Aktywność'),
      replacePolishChars('Waga'),
      replacePolishChars('Gorączka'),
      replacePolishChars('Notatki')
    ];
    const tableData = entries
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
      .map((e: Record<string, unknown>) => [
        e.date || '-',
        (e.mood !== undefined && Number(e.mood) > 0) ? `${e.mood}/5` : '-',
        (e.stress !== undefined && Number(e.stress) > 0) ? `${e.stress}/5` : '-',
        String((e.stressNote || '-')).substring(0, 12),
        (e.energy !== undefined && Number(e.energy) > 0) ? `${e.energy}/5` : '-',
        (e.sleepHours !== undefined && Number(e.sleepHours) > 0) ? String(e.sleepHours) : '-',
        (e.sleepQuality !== undefined && Number(e.sleepQuality) > 0) ? `${e.sleepQuality}/5` : '-',
        (e.abdominalPain !== undefined && Number(e.abdominalPain) > 0) ? `${e.abdominalPain}/5` : '-',
        (e.bloating !== undefined && Number(e.bloating) > 0) ? `${e.bloating}/5` : '-',
        (e.rumbling !== undefined && Number(e.rumbling) > 0) ? `${e.rumbling}/5` : '-',
        (e.heartburn !== undefined && Number(e.heartburn) > 0) ? `${e.heartburn}/5` : '-',
        (e.nausea !== undefined && Number(e.nausea) > 0) ? `${e.nausea}/5` : '-',
        (e.vomiting !== undefined && Number(e.vomiting) > 0) ? `${e.vomiting}/5` : '-',
        (e.lossOfAppetite !== undefined && Number(e.lossOfAppetite) > 0) ? `${e.lossOfAppetite}/5` : '-',
        (e.fullness !== undefined && Number(e.fullness) > 0) ? `${e.fullness}/5` : '-',
        e.erythemaNodosum ? 'Tak' : '-',
        e.pyodermaGangrenosum ? 'Tak' : '-',
        e.skinOther ? (e.skinOtherNote ? String(e.skinOtherNote).substring(0, 15) : 'Tak') : '-',
        e.eyeProblems ? 'Tak' : '-',
        e.mouthSores ? 'Tak' : '-',
        e.dietChange ? 'Tak' : '-',
        (e.mealHeaviness !== undefined && Number(e.mealHeaviness) > 0) ? `${e.mealHeaviness}/5` : '-',
        e.alcohol ? 'Tak' : '-',
        (e.physicalActivity !== undefined && Number(e.physicalActivity) > 0) ? `${e.physicalActivity}/5` : '-',
        e.weight ? String(e.weight) : '-',
        e.fever ? (e.temperature ? `${e.temperature}` : 'Tak') : '-',
        String(e.notes || '-').substring(0, 25)
      ]);

    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 40,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [107, 127, 255], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 8, right: 8 }
    });

    doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
  },

  // Eksport raportu "Stan choroby" do PDF
  exportDiseaseStateReportToPDF: (
    report: DiseaseStateReportData,
    dateFrom: string,
    dateTo: string,
    filename: string = 'raport_stan_choroby'
  ) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    let startY = 20;
    
    // Tytuł
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Raport - Stan choroby', 14, startY);
    startY += 10;
    
    // Data wygenerowania i okres
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const generatedDate = new Date().toLocaleString('pl-PL');
    doc.text(`Wygenerowano: ${generatedDate}`, 14, startY);
    startY += 6;
    doc.text(`Okres: ${dateFrom} - ${dateTo}`, 14, startY);
    startY += 6;
    doc.text(`Liczba dni: ${report.periodMetrics.daysTotal}`, 14, startY);
    startY += 10;
    
    // Podsumowanie
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Podsumowanie', 14, startY);
    startY += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(report.summaryText, 14, startY, { maxWidth: 180 });
    startY += 15;
    
    // Klasyfikacja stanu
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Klasyfikacja stanu', 14, startY);
    startY += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Stan: ${report.classification.stateLabel}`, 14, startY);
    startY += 6;
    doc.text(`Tendencja: ${report.classification.tendencyLabel}`, 14, startY);
    startY += 6;
    
    // Powody klasyfikacji
    if (report.classification.reasons.length > 0) {
      doc.text('Powody:', 14, startY);
      startY += 6;
      report.classification.reasons.forEach(reason => {
        doc.text(`• ${replacePolishChars(reason)}`, 20, startY);
        startY += 5;
      });
    }
    startY += 6;
    
    // Metryki okresu
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Metryki okresu', 14, startY);
    startY += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const metrics = [
      ['Wypróżnienia/dzień', report.periodMetrics.avgBmPerDay > 0 ? report.periodMetrics.avgBmPerDay.toFixed(1) : '—'],
      ['Dni z krwią', `${report.periodMetrics.daysWithBlood} (${report.periodMetrics.daysTotal > 0 ? Math.round((report.periodMetrics.daysWithBlood / report.periodMetrics.daysTotal) * 100) : 0}%)`],
      ['Dni bez krwi', `${report.periodMetrics.daysWithoutBlood} (${report.periodMetrics.daysTotal > 0 ? Math.round((report.periodMetrics.daysWithoutBlood / report.periodMetrics.daysTotal) * 100) : 0}%)`],
      ['Średni ból', report.periodMetrics.avgPain > 0 ? report.periodMetrics.avgPain.toFixed(1) : '—'],
      ['Średnia pilność', report.periodMetrics.avgUrgency > 0 ? report.periodMetrics.avgUrgency.toFixed(1) : '—'],
      ['Średnia ilość śluzu (gdy występuje)', report.periodMetrics.avgMucusWhenPresent > 0 ? report.periodMetrics.avgMucusWhenPresent.toFixed(1) : '—'],
      ['Średnia intensywność gazów (gdy występują)', report.periodMetrics.avgGasIntensityWhenPresent > 0 ? report.periodMetrics.avgGasIntensityWhenPresent.toFixed(1) : '—']
    ];
    
    metrics.forEach(([label, value]) => {
      doc.text(`${label}: ${value}`, 14, startY);
      startY += 6;
    });
    
    if (report.periodMetrics.worstDay) {
      startY += 3;
      doc.text(`Najgorszy dzień: ${report.periodMetrics.worstDay.date} (Trend: ${report.periodMetrics.worstDay.score})`, 14, startY);
      startY += 6;
    }
    
    if (report.periodMetrics.bestDay) {
      doc.text(`Najlepszy dzień: ${report.periodMetrics.bestDay.date} (Trend: ${report.periodMetrics.bestDay.score})`, 14, startY);
      startY += 10;
    }
    
    // Tabela dni (ostatnie 14 dni)
    if (report.daySummaries.length > 0) {
      if (startY > 250) {
        doc.addPage();
        startY = 20;
      }
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Ostatnie dni', 14, startY);
      startY += 8;
      
      const last14Days = report.daySummaries.slice(-14).reverse();
      const tableData = last14Days.map(day => [
        day.date,
        String(day.bmCount),
        day.bloodPresentDay ? `TAK${day.bloodMaxAmount ? ` (${day.bloodMaxAmount})` : ''}` : 'NIE',
        day.painAvgDay > 0 ? day.painAvgDay.toFixed(1) : '—',
        day.urgencyAvgDay > 0 ? day.urgencyAvgDay.toFixed(1) : '—',
        String(day.severityScore)
      ]);
      
      autoTable(doc, {
        head: [['Data', 'Wypróżnienia', 'Krew', 'Ból avg', 'Pilność avg', 'Trend']],
        body: tableData,
        startY: startY,
        styles: { 
          fontSize: 8,
          font: 'helvetica',
          fontStyle: 'normal',
          cellPadding: 2
        },
        headStyles: { 
          fillColor: [107, 127, 255], 
          textColor: 255,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 25 },
          2: { cellWidth: 30 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25 },
          5: { cellWidth: 20 }
        }
      });
    }
    
    doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
  },

  // Eksport raportu zdrowia jelit do PDF
  exportHealthReportToPDF: (
    report: HealthReportData,
    dateFrom: string,
    dateTo: string,
    filename: string = 'raport_zdrowia_jelit'
  ) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    let startY = 20;

    // Tytuł
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(replacePolishChars('Raport - Zdrowie jelit'), 14, startY);
    startY += 10;

    // Data wygenerowania i okres
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const generatedDate = new Date().toLocaleString('pl-PL');
    doc.text(`Wygenerowano: ${generatedDate}`, 14, startY);
    startY += 6;
    doc.text(`Okres: ${dateFrom} - ${dateTo}`, 14, startY);
    startY += 6;
    doc.text(`Liczba dni: ${report.dailyData.length}`, 14, startY);
    startY += 10;

    // Stan
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Monitor stanu', 14, startY);
    startY += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Stan: ${report.status.status} (ostatnie 7 dni)`, 14, startY);
    startY += 6;
    doc.text(`Porownanie do poprzednich 7 dni: ${replacePolishChars(report.trend)}`, 14, startY, { maxWidth: 180 });
    startY += 15;

    // Wzorce
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Wzorce przed pogorszeniem', 14, startY);
    startY += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (report.dailyData.length < 14) {
      doc.text('Za malo dni do analizy wzorcow (potrzeba min. 14 dni)', 14, startY);
      startY += 10;
    } else if (report.pogorszenieDays.length < 5) {
      doc.text('Za malo przypadkow pogorszenia do analizy wzorcow', 14, startY);
      startY += 10;
    } else {
      doc.text(`Znaleziono ${report.pogorszenieDays.length} dni z pogorszeniem.`, 14, startY);
      startY += 8;
      report.triggers.objawy.filter(t => t.count > 0).forEach(t => {
        doc.text(`  ${replacePolishChars(t.label)}: w ${t.count} na ${t.total} przypadkow`, 14, startY);
        startY += 6;
      });
      report.triggers.czynniki.filter(t => t.count > 0).forEach(t => {
        doc.text(`  ${replacePolishChars(t.label)}: w ${t.count} na ${t.total} przypadkow`, 14, startY);
        startY += 6;
      });
      startY += 4;
    }

    // Leki
    if (startY > 240) {
      doc.addPage();
      startY = 20;
    }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Leki', 14, startY);
    startY += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (report.medications.length === 0) {
      doc.text('Brak aktywnych lekow do analizy', 14, startY);
      startY += 10;
    } else {
      report.medications.forEach(m => {
        doc.setFont('helvetica', 'bold');
        doc.text(replacePolishChars(m.medication.name), 14, startY);
        startY += 6;
        doc.setFont('helvetica', 'normal');
        doc.text(`Rozpoczeta: ${m.medication.startDate}`, 14, startY);
        startY += 6;
        if (!m.hasEnoughBefore) {
          doc.text('Za malo danych sprzed rozpoczecia leku. Nie moge porownac przed/po.', 14, startY, { maxWidth: 180 });
          startY += 12;
          return;
        }
        doc.text(`Przed: wyproznienia sr. ${m.before14Days.avgBm.toFixed(1)}/dzen, krew ${m.before14Days.bloodDays}/14, obciazenie ${Math.round(m.before14Days.avgLoad)} pkt`, 14, startY, { maxWidth: 180 });
        startY += 6;
        doc.text(`Po: wyproznienia sr. ${m.after30Days.avgBm.toFixed(1)}/dzen, krew ${m.after30Days.bloodDays}/30, obciazenie ${Math.round(m.after30Days.avgLoad)} pkt`, 14, startY, { maxWidth: 180 });
        startY += 10;
      });
    }

    // Jakość danych
    if (startY > 240) {
      doc.addPage();
      startY = 20;
    }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Jakosc danych', 14, startY);
    startY += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Wyproznienia: ${report.dataQuality.bmDays} na ${report.dataQuality.totalDays} dni (${report.dataQuality.bmCoverage}%)`, 14, startY);
    startY += 6;
    doc.text(`Samopoczucie: ${report.dataQuality.trackingDays} na ${report.dataQuality.totalDays} dni (${report.dataQuality.trackingCoverage}%)`, 14, startY);
    startY += 6;
    doc.text(`Ocena: ${replacePolishChars(report.dataQuality.assessmentText)}`, 14, startY);

    doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
  }
};
